import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  InputBase,
  Typography,
  Fade,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';

// Imagens por categoria (placeholders/custom)
import imgADS from '../../assets/cursos/ADS.jpg';
import imgEstetica from '../../assets/cursos/Estetica.jpg';
import imgGastronomia from '../../assets/cursos/Gastronomia.jpg';
import imgGestaoComercial from '../../assets/cursos/Gestão-Comercial.jpg';
import imgGestaoPortuaria from '../../assets/cursos/Gestão-Portuária.jpg';
import imgGestaoRH from '../../assets/cursos/Gestão-RH.jpg';
import imgLogistica from '../../assets/cursos/Logística.jpg';
import imgMarketing from '../../assets/cursos/Marketing.jpg';
import imgMidiasDigitais from '../../assets/cursos/Mídias-Digitais.jpg';
import imgMidiasSociais from '../../assets/cursos/Mídias-Sociais.jpg';
import imgPodologia from '../../assets/cursos/Podologia.jpg';
import imgProcessosGerenciais from '../../assets/cursos/Processos-Gerenciais.jpg';
import imgInteligenciaArtificial from '../../assets/cursos/curso_ia.jpg';
import imgPlaceholder from '../../assets/cursos/placeholder.png';

const imgDesignModa = '/assets/cursos/Design-Moda.jpg';

// ===== Tipos =====
export type Turma = {
  id: number | string;
  nomeTurma: string;
  nomeUnidadeOperativa?: string;
  formaDeExecucao?: string;
  planoDoCursoId?: number | string;
  dataPeriodoExecucaoInicialFormatada?: string;
  informacoesDoPlanoDoCurso?: { tipoDeAcao?: string; nome?: string };
  horariosSemAbreviacao?: string[];
  enrollmentOpen?: boolean;      // decorado no backend
  linkInscricao?: string | null; // decorado no backend (quando aberta)
  linkInteresse?: string | null; // presente nas manuais/em breve
  editorial?: unknown;           // catálogo institucional publicado pelo backend
};

// ===== Utils =====
const getImagemCurso = (nome: string): string => {
  const nomeLower = nome.toLowerCase();
  if (nomeLower.includes('sistemas')) return imgADS;
  if (nomeLower.includes('design') && nomeLower.includes('moda')) return imgDesignModa;
  if (nomeLower.includes('estetica') || nomeLower.includes('estética')) return imgEstetica;
  if (nomeLower.includes('gastronomia')) return imgGastronomia;
  if (nomeLower.includes('comercial')) return imgGestaoComercial;
  if (nomeLower.includes('portuária') || nomeLower.includes('portuaria')) return imgGestaoPortuaria;
  if (nomeLower.includes('humanos')) return imgGestaoRH;
  if (nomeLower.includes('logística') || nomeLower.includes('logistica')) return imgLogistica;
  if (nomeLower.includes('marketing')) return imgMarketing;
  if (nomeLower.includes('digitais')) return imgMidiasDigitais;
  if (nomeLower.includes('sociais')) return imgMidiasSociais;
  if (nomeLower.includes('podologia')) return imgPodologia;
  if (nomeLower.includes('gerenciais')) return imgProcessosGerenciais;
  if (nomeLower.includes('artificial')) return imgInteligenciaArtificial;
  return imgPlaceholder;
};

const removerAcentos = (texto: string) => (texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const normaliza = (texto: string) => removerAcentos(texto).toLowerCase().trim();
const incluiAlgum = (base: string, termos: string[]) => termos.some((t) => base.includes(t));

// Palavras-chave para classificar
const KW_GRAD = [
  'graduacao', 'graduação', 'curso superior',
  'superior de tecnologia', 'tecnologo', 'tecnólogo',
  'bacharelado', 'licenciatura',
];
const KW_POS = [
  'pos', 'pós', 'pós-graduação', 'pos-graduação', 'pos graduação',
  'lato sensu', 'stricto sensu', 'especializacao', 'especialização',
  'mba', 'mestrado', 'doutorado',
];
const KW_EXTENSAO = ['extensao', 'extensão'];

// ===== Prefixo e normalização de nomes =====
const CST_PREFIX = 'Curso Superior de Tecnologia em ';

// Adiciona o prefixo apenas quando fizer sentido (evita duplicar para nomes que já são Curso Superior/Bacharelado/Licenciatura)
const addCSTPrefix = (nome: string): string => {
  const n = (nome || '').trim();
  const nn = normaliza(n);
  if (nn.startsWith('curso superior')) return n;     // já tem prefixo
  if (nn.startsWith('bacharelado')) return n;        // bacharel
  if (nn.startsWith('licenciatura')) return n;       // licenciatura
  return `${CST_PREFIX}${n}`;
};

// Gera a chave de comparação padronizada (sempre com prefixo, sem acentos, minúscula)
const canonicalKey = (nome: string): string => normaliza(addCSTPrefix(nome));

// Cursos extintos ou que não fazem mais parte do portfólio não devem reaparecer via API.
const CURSOS_FORA_PORTFOLIO = new Set([
  canonicalKey('Curso Superior de Tecnologia em Gestão Hospitalar'),
  // Não consta no catálogo institucional de graduação recebido para publicação.
  canonicalKey('Curso Superior de Tecnologia em Marketing'),
]);

const dataInicioValue = (data?: string): number => {
  const [dia, mes, ano] = (data || '').split('/').map(Number);
  return dia && mes && ano ? Date.UTC(ano, mes - 1, dia) : 0;
};

const valorNumerico = (valor?: number | string): number => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const compararOfertasMaisRecentes = (a: Turma, b: Turma): number =>
  dataInicioValue(b.dataPeriodoExecucaoInicialFormatada) - dataInicioValue(a.dataPeriodoExecucaoInicialFormatada)
  || valorNumerico(b.planoDoCursoId) - valorNumerico(a.planoDoCursoId)
  || valorNumerico(b.id) - valorNumerico(a.id);

const unidadeKey = (turma: Turma): string => {
  const unidade = normaliza(turma.nomeUnidadeOperativa || '');

  if (unidade.includes('itacoatiara')) return 'itacoatiara';
  if (unidade.includes('parintins')) return 'parintins';
  if (unidade.includes('maues')) return 'maues';
  if (unidade.includes('manaus') || unidade.includes('faculdade de tecnologia senac')) return 'manaus';

  return unidade || 'unidade-nao-informada';
};

const contarUnidades = (variacoes: Turma[]) => {
  const unidades = new Map<string, boolean>();

  variacoes.forEach((turma) => {
    const key = unidadeKey(turma);
    const aberta = unidades.get(key) || Boolean(turma.enrollmentOpen);
    unidades.set(key, aberta);
  });

  return {
    total: unidades.size,
    abertas: Array.from(unidades.values()).filter(Boolean).length,
  };
};

// ===== Estilos =====
const SearchInput = styled(InputBase)({
  padding: '14px 18px',
  width: '100%',
  maxWidth: 480,
  border: '1px solid #ccc',
  borderRadius: 10,
  fontSize: '1rem',
});

const CardCurso = styled(Box)<{ imagem: string }>(({ imagem }) => ({
  position: 'relative',
  borderRadius: 12,
  overflow: 'hidden',
  backgroundImage: `url(${imagem})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: 400,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: 24,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease',
  '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' },
  color: '#fff',
}));

const Overlay = styled('div')({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.9))',
  zIndex: 1,
});

const CardContent = styled(Box)({ position: 'relative', zIndex: 2 });

// ===== Componente =====
const Graduacao: React.FC = () => {
  const [turmasAgrupadas, setTurmasAgrupadas] = useState<Record<string, any>>({});
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [, setAtualizadoEm] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCursos = async () => {
      setLoading(true);

      const isProd = process.env.NODE_ENV === 'production';
      const bases = isProd
        ? ['/api/turmas'] // produção: same-origin via proxy/Apache
        // No ambiente local, prioriza a API em execução. As rotas relativas podem
        // apontar para uma resposta antiga ou para o fallback do React.
        : ['http://localhost:5000/turmas', 'http://localhost:5000/api/turmas', '/api/turmas', '/turmas'];

      let payload: any = null;
      let lastErr: any = null;

      for (const url of bases) {
        try {
          const resp = await axios.get(url, { timeout: 20000 });
          const candidate = resp.data;
          // Em desenvolvimento, /turmas pode ser devolvido pelo React como HTML
          // (fallback da SPA). Só aceita respostas que tenham o formato do catálogo.
          if (Array.isArray(candidate?.turmasAtivas) || Array.isArray(candidate?.turmasEmBreve)) {
            payload = candidate;
            break;
          }
          console.warn('Resposta inválida ao buscar cursos em', url);
        } catch (err) {
          lastErr = err;
          console.warn('Falha ao buscar em', url, err);
        }
      }

      if (!payload) {
        console.error('Erro ao carregar cursos (todas URLs falharam):', lastErr);
        setLoading(false);
        return;
      }

      setAtualizadoEm(payload.atualizadoEm || null);

      // ===== Filtro: SOMENTE Graduação =====
      const isGraduacao = (t: Turma) => {
        const nomesCurso = [t.informacoesDoPlanoDoCurso?.nome, t.nomeTurma].filter(Boolean) as string[];
        if (nomesCurso.some((nome) => CURSOS_FORA_PORTFOLIO.has(canonicalKey(nome)))) return false;

        const tipo = normaliza(t.informacoesDoPlanoDoCurso?.tipoDeAcao || '');
        const nomePlano = normaliza(t.informacoesDoPlanoDoCurso?.nome || '');
        const nomeTurma = normaliza(t.nomeTurma || '');
        const base = `${tipo} ${nomePlano} ${nomeTurma}`;

        if (incluiAlgum(base, KW_POS) || incluiAlgum(base, KW_EXTENSAO)) return false;
        if (incluiAlgum(base, KW_GRAD)) return true;
        if (base.includes('tecnologia em') || base.includes('curso superior')) return true;
        return false;
      };

      // Separa em ATIVAS e OUTRAS (em breve/manuais)
      const ativasRaw: Turma[] = (payload.turmasAtivas || []).filter(isGraduacao);
      const outrasRaw: Turma[] = (payload.turmasEmBreve || []).filter(isGraduacao);

      // Prepara ATIVAS: aplica prefixo no nome base de exibição e comparação
      const ativas = ativasRaw.map((t) => {
        const nomeFonte = t.informacoesDoPlanoDoCurso?.nome || t.nomeTurma;
        const nomeBase = addCSTPrefix(nomeFonte); // ✅ prefixo nas ativas
        return { ...t, __nomeFonte: nomeFonte, __nomeBase: nomeBase, __isAtiva: true };
      });

      // Prepara OUTRAS: NÃO aplica prefixo no texto, mas a comparação usará canonicalKey
      const outras = outrasRaw.map((t) => {
        const nomeFonte = t.informacoesDoPlanoDoCurso?.nome || t.nomeTurma;
        const nomeBase = nomeFonte; // sem prefixo visual aqui
        return { ...t, __nomeFonte: nomeFonte, __nomeBase: nomeBase, __isAtiva: false };
      });

      // Conjunto das chaves canônicas das ATIVAS
      const activeKeys = new Set(ativas.map((t) => canonicalKey(t.__nomeBase)));

      // OUTRAS filtradas: somem quando houver ativa equivalente (comparação canônica com prefixo)
      const outrasFiltradas = outras.filter((t) => !activeKeys.has(canonicalKey(t.__nomeBase)));

      // Junta e agrupa
      const turmas: any[] = [...ativas, ...outrasFiltradas];

      const agrupadas: Record<string, any> = {};
      turmas.forEach((turma) => {
        // chave de agrupamento sempre canônica com prefixo
        const key = canonicalKey(turma.__nomeBase);
        if (!agrupadas[key]) {
          const nomeBaseExibicao = addCSTPrefix(turma.__nomeBase); // garante exibição com prefixo
          agrupadas[key] = {
            key,
            nomeBase: nomeBaseExibicao,
            imagem: getImagemCurso(nomeBaseExibicao),
            variacoes: [turma],
          };
        } else {
          agrupadas[key].variacoes.push(turma);
        }
      });

      Object.values(agrupadas).forEach((curso: any) => {
        curso.variacoes.sort(compararOfertasMaisRecentes);
      });

      setTurmasAgrupadas(agrupadas);
      setTimeout(() => setLoading(false), 600);
    };

    fetchCursos();
  }, []);

  const termosBusca = useMemo(() => normaliza(busca).split(' ').filter(Boolean), [busca]);

  const cursosFiltrados = useMemo(() => {
    const lista: any[] = Object.values(turmasAgrupadas);
    return lista.filter((curso: any) =>
      termosBusca.every((termo) => normaliza(curso.nomeBase).includes(termo))
    );
  }, [turmasAgrupadas, termosBusca]);

  const formatListaExecucao = (variacoes: Turma[]) => {
    const set = new Set<string>();
    variacoes.forEach((v: any) => {
      if (v.formaDeExecucao) {
        let forma = v.formaDeExecucao.trim();
        // Normaliza "Flexível" -> "Presencial"
        if (normaliza(forma) === 'flexivel') forma = 'Presencial';
        set.add(forma);
      }
    });
    return Array.from(set).join(', ');
  };

  const firstIdOrUndefined = (variacoes: Turma[]) =>
    variacoes?.[0]?.id ? String((variacoes as any)[0].id) : undefined;

  return (
    <Container sx={{ py: 8 }}>
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Typography variant="h4" align="center" gutterBottom fontWeight={600}>
        Cursos de Graduação
      </Typography>

      <Box textAlign="center" mb={6}>
        <SearchInput
          placeholder="Buscar curso por nome..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Box>

      {!loading && (
        <>
          {cursosFiltrados.length === 0 ? (
            <Typography align="center">Nenhum curso encontrado.</Typography>
          ) : (
            <Fade in timeout={600}>
              <Grid container spacing={4}>
                {cursosFiltrados.map((curso: any, index: number) => {
                  const { total, abertas } = contarUnidades(curso.variacoes);
                  const temAberta = abertas > 0;
                  const idPrimeira = firstIdOrUndefined(curso.variacoes);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={curso.key ?? index}>
                      <CardCurso imagem={curso.imagem}>
                        <Overlay />
                        <CardContent>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={temAberta ? 'Matrículas abertas' : 'Registre seu interesse'}
                              size="small"
                              sx={{
                                bgcolor: temAberta ? 'success.main' : 'warning.main',
                                color: '#fff',
                                fontWeight: 600,
                              }}
                            />
                            <Chip
                              label={`${abertas}/${total} unidade(s)`}
                              size="small"
                              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                            />
                          </Box>

                          <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
                            {curso.nomeBase}
                          </Typography>
                          <Typography sx={{ opacity: 0.9 }}>
                            {formatListaExecucao(curso.variacoes)}
                          </Typography>

                          <Button
                            variant="contained"
                            sx={{ mt: 2, background: 'linear-gradient(45deg, #6b46c1, #9f7aea)' }}
                            disabled={!idPrimeira}
                            onClick={() =>
                              idPrimeira &&
                              navigate(`/curso-graduacao/${idPrimeira}`, {
                                state: { variacoes: curso.variacoes },
                              })
                            }
                          >
                            Mais Informações
                          </Button>
                        </CardContent>
                      </CardCurso>
                    </Grid>
                  );
                })}
              </Grid>
            </Fade>
          )}
        </>
      )}
    </Container>
  );
};

export default Graduacao;
